// Gestión del roster por parte del organizador.

import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from '../query-client';
import {
  Nullable,
  Tournament,
  TournamentPlayer,
  User,
  UserWithToken,
  WithData,
} from '../api.types';
import { USER_BASIC_FIELDS, USER_WITH_COSSY } from '../fragments';
import { RoundResultInput } from './public';

export const addPlayer = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  userId: number,
  markUnplayedAs?: 'WIN' | 'LOSS' | 'NONE',
  pairWithMatchId?: number,
  roundResults?: RoundResultInput[],
  invited?: boolean
) =>
  getGraphQLQueryClient(user).request<WithData<TournamentPlayer>>(
    gql`
      ${USER_BASIC_FIELDS}
      mutation (
        $tournamentId: Int!
        $userId: Int!
        $invited: Boolean
        $markUnplayedAs: MarkUnplayedAs
        $pairWithMatchId: Int
        $roundResults: [RoundResultInput!]
      ) {
        data: enrollPlayer(
          tournamentId: $tournamentId
          userId: $userId
          invited: $invited
          markUnplayedAs: $markUnplayedAs
          pairWithMatchId: $pairWithMatchId
          roundResults: $roundResults
        ) {
          id
          user {
            ...UserBasicFields
          }
        }
      }
    `,
    { tournamentId, userId, invited, markUnplayedAs, pairWithMatchId, roundResults }
  );

export type EnrollPlayersBatchResult = {
  enrolled: Array<{
    id: number;
    user: {
      id: number;
      name: string;
      lastName: string | null;
    };
  }>;
  errors: Array<{
    userId: number;
    message: string;
  }>;
};

export const addPlayersBatch = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  userIds: number[],
  markUnplayedAs?: 'WIN' | 'LOSS' | 'NONE',
  invited?: boolean
) =>
  getGraphQLQueryClient(user).request<{ data: EnrollPlayersBatchResult }>(
    gql`
      ${USER_BASIC_FIELDS}
      mutation enrollPlayersBatch(
        $tournamentId: Int!
        $userIds: [Int!]!
        $invited: Boolean
        $markUnplayedAs: MarkUnplayedAs
      ) {
        data: enrollPlayersBatch(
          tournamentId: $tournamentId
          userIds: $userIds
          invited: $invited
          markUnplayedAs: $markUnplayedAs
        ) {
          enrolled {
            id
            user {
              ...UserBasicFields
            }
          }
          errors {
            userId
            message
          }
        }
      }
    `,
    { tournamentId, userIds, invited, markUnplayedAs }
  );

export const acceptPlayer = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  userId: number
) =>
  getGraphQLQueryClient(user).request<WithData<TournamentPlayer>>(
    gql`
      ${USER_BASIC_FIELDS}
      mutation ($tournamentId: Int!, $userId: Int!) {
        data: acceptPlayer(tournamentId: $tournamentId, userId: $userId) {
          id
          status
          user {
            ...UserBasicFields
          }
        }
      }
    `,
    { tournamentId, userId }
  );

export const rejectPlayer = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  userId: number,
  reason?: string
) =>
  getGraphQLQueryClient(user).request<WithData<TournamentPlayer>>(
    gql`
      ${USER_BASIC_FIELDS}
      mutation ($tournamentId: Int!, $userId: Int!, $reason: String) {
        data: rejectPlayer(
          tournamentId: $tournamentId
          userId: $userId
          reason: $reason
        ) {
          id
          status
          rejectionReason
          user {
            ...UserBasicFields
          }
        }
      }
    `,
    { tournamentId, userId, reason }
  );

export const removePlayer = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  userId: number
) =>
  getGraphQLQueryClient(user).request<WithData<Tournament>>(
    gql`
      mutation ($tournamentId: Int!, $userId: Int!) {
        data: removePlayer(tournamentId: $tournamentId, userId: $userId) {
          id
        }
      }
    `,
    { tournamentId, userId }
  );

export const createPlayer = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  input: {
    cossyId?: string;
    name: string;
    lastName: string;
    topDeckId?: string;
    topDeckNick?: string;
    limitlessId?: string;
    limitlessNick?: string;
  },
  markUnplayedAs?: 'WIN' | 'LOSS' | 'NONE',
  roundResults?: RoundResultInput[],
  invited?: boolean,
  pairWithMatchId?: number
) =>
  getGraphQLQueryClient(user).request<WithData<TournamentPlayer>>(
    gql`
      ${USER_WITH_COSSY}
      mutation (
        $tournamentId: Int!
        $input: CreatePlayerInput!
        $invited: Boolean
        $markUnplayedAs: MarkUnplayedAs
        $pairWithMatchId: Int
        $roundResults: [RoundResultInput!]
      ) {
        data: createPlayer(
          tournamentId: $tournamentId
          input: $input
          invited: $invited
          markUnplayedAs: $markUnplayedAs
          pairWithMatchId: $pairWithMatchId
          roundResults: $roundResults
        ) {
          id
          user {
            ...UserWithCossy
          }
        }
      }
    `,
    { tournamentId, input, invited, markUnplayedAs, pairWithMatchId, roundResults }
  );

export const createPlayersBatch = async (
  user: Nullable<UserWithToken>,
  tournamentId: number,
  players: {
    cossyId?: string;
    name: string;
    lastName: string;
    topDeckId?: string;
    topDeckNick?: string;
    limitlessId?: string;
    limitlessNick?: string;
  }[],
  invited?: boolean
) =>
  getGraphQLQueryClient(user).request<{
    data: {
      created: { id: number; user: User }[];
      errors: { cossyId: string | null; message: string }[];
    };
  }>(
    gql`
      ${USER_WITH_COSSY}
      mutation createPlayersBatch(
        $tournamentId: Int!
        $players: [CreatePlayerInput!]!
        $invited: Boolean
      ) {
        data: createPlayersBatch(
          tournamentId: $tournamentId
          players: $players
          invited: $invited
        ) {
          created {
            id
            user {
              ...UserWithCossy
            }
          }
          errors {
            cossyId
            message
          }
        }
      }
    `,
    { tournamentId, players, invited }
  );

export const createPlayerOnly = async (
  user: Nullable<UserWithToken>,
  organizerId: number,
  input: {
    cossyId: string;
    name: string;
    lastName: string;
  }
) =>
  getGraphQLQueryClient(user).request<WithData<User>>(
    gql`
      ${USER_WITH_COSSY}
      mutation ($organizerId: Int!, $input: CreatePlayerInput!) {
        data: createPlayerOnly(organizerId: $organizerId, input: $input) {
          ...UserWithCossy
        }
      }
    `,
    { organizerId, input }
  );
